import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useWallet } from '../hooks/useWallet';
import { ADDR, NET } from '../config';
import { contract, fmt, getLogs, loadShips, chainNow, tierLabel, shipImg, reasonText } from '../lib';
import SeasonPanel from '../components/SeasonPanel';

// Dashboard v2: one obvious action per ship, everything else behind ⋯ or an ⓘ tooltip. Every action reports a plain-English
// result in the status bar (no silent returns). Approvals happen automatically as "Step 1 of 2".

const dur = (sec) => {
  if (sec <= 0) return 'now';
  const h = Math.floor(sec / 3600); const m = Math.ceil((sec % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m} min`;
};

export function Info({ children, label = 'How this works' }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="af-info" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button type="button" className="af-info-btn" aria-label={label} onClick={() => setOpen((o) => !o)}>i</button>
      {open && <span className="af-tip" role="tooltip">{children}</span>}
    </span>
  );
}

function Modal({ title, onClose, children, testid }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="cm-overlay" data-testid={testid} onClick={onClose}>
      <div className="cm-box" onClick={(e) => e.stopPropagation()}>
        <div className="cm-head"><h3>{title}</h3><button className="cm-x" aria-label="Close" onClick={onClose}>✕</button></div>
        {children}
      </div>
    </div>
  );
}

const MAX_CLAIM = 20; // Fleet.MAX_CLAIM_SHIPS: bigger collects are split into batches
async function readReq(f, hub, id, total) {
  const r = await f.getRequest(id);
  return { id, total, route: Number(r.route), unstake: r.unstake, settled: r.settled, n: r.ids.length, at: Number(r.requestedAt),
    word: r.settled || (await hub.randomOf(r.randId)) !== 0n };
}

function Dashboard() {
  const { account, send, run, nonce, connect, ore, setStatus, refresh } = useWallet();
  const [ships, setShips] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [gen1, setGen1] = useState({ n: 1 });
  const [g1pend, setG1pend] = useState([]);
  const [stats, setStats] = useState(null);
  const [now, setNow] = useState(Date.now() / 1000);
  const [mined, setMined] = useState(null);
  const [g1tick, setG1tick] = useState(0);
  const [rtick, setRtick] = useState(0);
  const [menu, setMenu] = useState(null); // ship id whose ⋯ menu is open
  const [collect, setCollect] = useState(null); // { title, miners:[ids], pirates:[ids], unstake }
  const [route, setRoute] = useState(0);
  const [sell, setSell] = useState(null); // { ship, price }
  const [usdPerBnb, setUsdPerBnb] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const f = contract('Fleet'); const y = contract('Shipyard');
    chainNow().then(setNow).catch(() => {});
    Promise.all([f.totalPower(), f.totalRankWeight(), f.minStake(), f.totalPaidMiners(), f.totalFuelBurned(), y.gen1Start(), y.gen1Requested(), f.rules(), y.gen1Delay(), y.hijackBps(), f.retryDelay()])
      .then(([tp, rw, ms, paid, fuel, g1s, g1r, ru, g1d, hj, rd]) => setStats({ tp, rw, ms: Number(ms), rd: Number(rd), paid, fuel, g1s: Number(g1s), g1r: Number(g1r), g1d: Number(g1d),
        fuelBps: Number(ru.fuelBps), fuelMin: ru.fuelMin, safeTax: Number(ru.safeTaxBps) / 100, riskyTax: Number(ru.riskyTaxBps) / 100, intercept: Number(ru.interceptBps) / 100, hijack: Number(hj) / 100 }))
      .catch(() => {});
    contract('Genesis').usdToWei(10n ** 18n).then((w) => setUsdPerBnb(1e18 / Number(w))).catch(() => {});
    if (!account) return;
    setShips(null);
    loadShips(account).then(setShips).catch(() => { setShips([]); setStatus({ kind: 'err', text: 'Could not load your ships: the network is busy. Refresh the page in a moment.' }); });
    const hub = contract('RandomnessHub');
    (async () => {
      const evs = await getLogs(f, f.filters.ReturnRequested(null, account));
      setReqs((await Promise.all(evs.map((ev) => readReq(f, hub, ev.args[0], ev.args[4])))).reverse());
      const mine = new Set(evs.map((ev) => String(ev.args[0])));
      const [rets, pcs] = await Promise.all([getLogs(f, f.filters.ShipReturned()), getLogs(f, f.filters.PirateClaimed(account))]);
      setMined(rets.filter((ev) => mine.has(String(ev.args[0]))).reduce((t, ev) => t + ev.args[2], 0n) + pcs.reduce((t, ev) => t + ev.args[2], 0n));
    })().catch(() => {});
  }, [account, nonce, setStatus]);

  // Claims settle by themselves when the dice roll arrives (v8.4). Poll open ones; refresh balances when one lands.
  useEffect(() => {
    if (!account || !reqs.some((r) => !r.settled)) return undefined;
    const t = setTimeout(async () => {
      const f = contract('Fleet'); const hub = contract('RandomnessHub');
      try {
        const fresh = await Promise.all(reqs.map((r) => (r.settled ? r : readReq(f, hub, r.id, r.total))));
        if (fresh.some((r, i) => r.settled && !reqs[i].settled)) refresh(); // ORE arrived: reload wallet + fleet
        else setReqs(fresh);
      } catch (e) { /* network busy: try again next tick */ }
      setRtick((x) => x + 1);
    }, 4000);
    return () => clearTimeout(t);
  }, [account, reqs, rtick, refresh]);

  // Gen1 builds (separate so polling does not reload the fleet).
  useEffect(() => {
    if (!account) return;
    const y = contract('Shipyard');
    (async () => {
      const gevs = await getLogs(y, y.filters.Gen1Requested(null, account));
      const rows = (await Promise.all(gevs.map(async (ev) => {
        const [p, st] = await Promise.all([y.pending(ev.args[0]), y.mintStatus(ev.args[0])]);
        return { pid: ev.args[0], count: Number(p.count), status: Number(st) }; // 1 drawing, 2 drawn, 3 delivered (automatic)
      }))).reverse();
      setG1pend((old) => {
        const was = new Set(old.filter((p) => p.status !== 3).map((p) => String(p.pid)));
        if (rows.some((p) => p.status === 3 && was.has(String(p.pid)))) loadShips(account).then(setShips).catch(() => {});
        return rows;
      });
    })().catch(() => {});
  }, [account, nonce, g1tick]);

  useEffect(() => {
    if (!g1pend.some((p) => p.status !== 3)) return undefined;
    const t = setTimeout(() => setG1tick((x) => x + 1), 5000);
    return () => clearTimeout(t);
  }, [g1pend]);

  useEffect(() => {
    if (!stats) return;
    contract('Shipyard').quoteFull(gen1.n).then((q) => setGen1((g) => ({ ...g, ok: q[0], why: q[1], cost: q[2], usd: q[3] }))).catch(() => setGen1((g) => ({ ...g, ok: false, why: 'NetworkBusy' })));
  }, [stats, gen1.n]);

  useEffect(() => { const t = setInterval(() => setNow((n) => n + 1), 1000); return () => clearInterval(t); }, []);

  if (!account) return (<div className="dashboard-container"><h1>Your Fleet</h1><button className="mint-btn" onClick={connect}>Connect Wallet</button></div>);

  const list = ships || [];
  const minStake = stats?.ms ?? 86400;
  const readyIn = (sh) => Number(sh.stake.since) + minStake - now; // seconds until a miner can be collected / brought home
  const locked = (sh) => sh.stake && sh.stake.req !== 0n;
  const idleShips = list.filter((s) => s.where === 'wallet');
  const readyMiners = list.filter((s) => s.where === 'staked' && s.kind === 'Miner' && !locked(s) && readyIn(s) <= 0);
  const stakedPirates = list.filter((s) => s.where === 'staked' && s.kind === 'Pirate');
  const hasMiners = list.some((s) => s.kind === 'Miner'); const hasPirates = list.some((s) => s.kind === 'Pirate');
  const job = (sh) => (sh.kind === 'Pirate' ? 'raid' : 'mine');

  // Wrap a whole user action: one busy flag, every failure shown in plain words.
  const act = (label, fn) => async () => {
    if (busy) return;
    setBusy(true); setMenu(null);
    try { await run(label, fn); } finally { setBusy(false); }
  };
  // Approval is an automatic first step. Returns false if the user cancelled / it failed (status already explains why).
  const allowShips = async (op, total) => {
    if (await contract('Ships').isApprovedForAll(account, op)) return true;
    return !!(await send(`Step 1 of ${total}: allow the game to use your ships`, (s) => contract('Ships', s).setApprovalForAll(op, true)));
  };
  const allowOre = async (amt, total) => {
    if ((await contract('ORE').allowance(account, ADDR.Shipyard)) >= amt) return true;
    return !!(await send(`Step 1 of ${total}: allow the shipyard to use your ORE`, (s) => contract('ORE', s).approve(ADDR.Shipyard, ethers.MaxUint256)));
  };
  const checkOre = (need) => { if (ore < need) throw new Error(`You need ${fmt(need, 0)} ORE but have ${fmt(ore, 0)} ORE.`); };

  const sendToWork = (ships_, label) => act(label, async () => {
    if (!ships_.length) throw new Error('No ships in your hangar to send.');
    const approved = await contract('Ships').isApprovedForAll(account, ADDR.Fleet);
    if (!approved && !(await allowShips(ADDR.Fleet, 2))) return;
    await send(approved ? label : `Step 2 of 2: ${label.toLowerCase()}`, (s) => contract('Fleet', s).stake(ships_.map((x) => x.id)));
  });
  const doCollect = act('Collect', async () => {
    const c = collect; setCollect(null);
    const steps = Math.ceil(c.miners.length / MAX_CLAIM) + (c.pirates.length ? 1 : 0);
    let k = 0; const pre = () => (steps > 1 ? `Step ${++k} of ${steps}: ` : '');
    if (c.miners.length) {
      const verb = c.unstake ? 'bring home' : 'collect ORE from';
      const parts = []; for (let i = 0; i < c.miners.length; i += MAX_CLAIM) parts.push(c.miners.slice(i, i + MAX_CLAIM));
      for (let j = 0; j < parts.length; j++) {
        const batch = parts.length > 1 ? ` (batch ${j + 1} of ${parts.length})` : '';
        const ok = await send(`${pre()}${verb} ${parts[j].length} miner${parts[j].length > 1 ? 's' : ''} (${route ? 'gamble' : 'safe'})${batch}`, (s) => contract('Fleet', s).requestReturn(parts[j], route, c.unstake));
        if (!ok) return;
      }
    }
    if (c.pirates.length) await send(`${pre()}${c.unstake ? 'bring home' : 'collect loot from'} ${c.pirates.length} pirate${c.pirates.length > 1 ? 's' : ''}`, (s) => contract('Fleet', s).claimPirates(c.pirates, c.unstake));
  });
  const openCollect = (title, miners, pirates, unstake) => {
    if (!miners.length && !pirates.length) { setStatus({ kind: 'err', text: `${title}: nothing to collect yet. Miners can be collected ${dur(minStake)} after they start mining.` }); return; }
    setRoute(0); setMenu(null);
    if (!miners.length) { // pirates only: no safe/gamble choice needed
      act(title, async () => { await send(`${unstake ? 'Bring home' : 'Collect loot from'} ${pirates.length} pirate${pirates.length > 1 ? 's' : ''}`, (s) => contract('Fleet', s).claimPirates(pirates, unstake)); })();
      return;
    }
    setCollect({ title, miners, pirates, unstake });
  };
  // Fallbacks only, for a claim that did not settle by itself.
  const STUCK_SECS = 60;
  const stuck = (r) => (r.word ? (now - r.at > STUCK_SECS ? 'settle' : null) : (now - r.at > (stats?.rd ?? 3600) ? 'retry' : null));
  const receive = (r) => act('Receive ORE', async () => {
    if (stuck(r) === 'retry') await send('Roll the dice again for your ORE', (s) => contract('Fleet', s).retry(r.id));
    else await send('Receive your ORE', (s) => contract('Fleet', s).settle(r.id));
  })();
  const upgrade = (sh) => act(`Upgrade #${sh.id}`, async () => {
    const c = await contract('Shipyard').upgradeCost(sh.level); checkOre(c);
    const approved = (await contract('ORE').allowance(account, ADDR.Shipyard)) >= c;
    if (!approved && !(await allowOre(c, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}upgrade ship #${sh.id} for ${fmt(c, 0)} ORE`, (s) => contract('Shipyard', s).upgrade(sh.id));
  })();
  const repair = (sh) => act(`Repair #${sh.id}`, async () => {
    const [cpp, maxD] = await Promise.all([contract('Shipyard').repairCostPerPoint(), contract('Ships').maxDurability()]);
    const c = cpp * BigInt(Number(maxD) - sh.durability); checkOre(c);
    const approved = (await contract('ORE').allowance(account, ADDR.Shipyard)) >= c;
    if (!approved && !(await allowOre(c, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}repair ship #${sh.id} for ${fmt(c, 0)} ORE`, (s) => contract('Shipyard', s).repair(sh.id));
  })();
  const doSell = act('Sell', async () => {
    const { ship, price } = sell;
    let wei; try { wei = ethers.parseEther(String(price).trim()); } catch (_) { throw new Error('Enter a price in BNB, for example 0.05.'); }
    if (wei <= 0n) throw new Error('Enter a price above 0.');
    setSell(null);
    const approved = await contract('Ships').isApprovedForAll(account, ADDR.Marketplace);
    if (!approved && !(await allowShips(ADDR.Marketplace, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}put ship #${ship.id} up for sale at ${fmt(wei)} ${NET.currency}`, (s) => contract('Marketplace', s).list(ship.id, wei));
  });
  const cancelSale = (sh) => act(`Cancel sale #${sh.id}`, async () => { await send(`Take ship #${sh.id} off sale`, (s) => contract('Marketplace', s).cancel(sh.id)); })();
  const mintGen1 = act('Build Gen1 ship', async () => {
    const q = await contract('Shipyard').quoteFull(gen1.n);
    if (!q[0]) throw new Error(reasonText(q[1]));
    const maxOre = (q[2] * 103n) / 100n; // 3% slippage guard; the contract burns the exact price at mint time
    checkOre(q[2]);
    const approved = (await contract('ORE').allowance(account, ADDR.Shipyard)) >= maxOre;
    if (!approved && !(await allowOre(maxOre, 2))) return;
    await send(`${approved ? '' : 'Step 2 of 2: '}build ${gen1.n} Gen1 ship${gen1.n > 1 ? 's' : ''}`, (s) => contract('Shipyard', s).mintGen1(gen1.n, maxOre));
  });

  const stateLine = (sh) => {
    if (sh.where === 'wallet') return 'In your hangar';
    if (sh.where === 'listed') return `For sale · ${fmt(sh.listing.price)} ${NET.currency}`;
    if (locked(sh)) return 'Coming back with cargo…';
    return sh.kind === 'Pirate' ? 'Out raiding' : 'Out mining';
  };
  const mainAction = (sh) => {
    if (sh.where === 'wallet') return <button className="action-btn" disabled={busy} onClick={sendToWork([sh], `Send ship #${sh.id} to ${job(sh)}`)}>Send to {job(sh)}</button>;
    if (sh.where === 'listed') return <button className="action-btn" disabled={busy} onClick={() => cancelSale(sh)}>Cancel sale</button>;
    if (sh.kind === 'Pirate') return <button className="action-btn" disabled={busy} onClick={() => openCollect('Collect loot', [], [sh.id], false)}>Collect loot</button>;
    if (locked(sh)) return <button className="action-btn" disabled title="This ship is on its way back. See 'On the way' below.">On the way…</button>;
    const wait = readyIn(sh);
    if (wait > 0) return <button className="action-btn" disabled title="Miners need some time out before they can come back">Collect in {dur(wait)}</button>;
    return <button className="action-btn" disabled={busy} onClick={() => openCollect('Collect ORE', [sh.id], [], false)}>Collect ORE</button>;
  };
  const moreItems = (sh) => {
    const items = [];
    if (sh.where === 'staked' && !locked(sh)) {
      if (sh.kind === 'Pirate') items.push(['Bring home', () => openCollect('Bring home', [], [sh.id], true)]);
      else items.push(['Bring home', () => (readyIn(sh) > 0 ? setStatus({ kind: 'err', text: `Ship #${sh.id} can come home in ${dur(readyIn(sh))}.` }) : openCollect('Bring home', [sh.id], [], true))]);
    }
    if (sh.where === 'wallet') items.push(['Sell', () => { setMenu(null); setSell({ ship: sh, price: '0.05' }); }]);
    if (sh.where === 'wallet' && sh.kind === 'Miner') items.push(['Upgrade', () => upgrade(sh)]);
    if (sh.durability < 10 && sh.where !== 'listed') items.push(['Repair', () => repair(sh)]);
    return items;
  };

  const claimable = list.filter((s) => s.where === 'staked').reduce((t, s) => t + s.pending, 0n);
  const openReqs = reqs.filter((r) => !r.settled);
  const sellUsd = sell && usdPerBnb && Number(sell.price) > 0 ? Number(sell.price) * usdPerBnb : null;

  return (
    <div className="dashboard-container" style={{ maxWidth: 1100 }}>
      <h1>Your Fleet {stats && <Info label="Game totals">Fleet power {String(stats.tp)} · pirate rank weight {String(stats.rw)} · paid to miners {fmt(stats.paid, 0)} ORE · fuel burned {fmt(stats.fuel, 0)} ORE</Info>}</h1>
      <SeasonPanel fleet={{
        power: list.filter((s) => s.where === 'staked' && s.kind === 'Miner').reduce((t, s) => t + BigInt(s.stake.weight), 0n),
        claimable, mined }} />

      {ships !== null && ships.length > 0 && (
        <div className="card af-top" data-testid="top-actions">
          <div>
            <span className="price">{fmt(claimable, 0)} ORE</span> <span style={{ color: 'var(--muted)' }}>waiting to be collected</span>
            {readyMiners.length + stakedPirates.length === 0 && list.some((s) => s.where === 'staked' && s.kind === 'Miner')
              && <span style={{ color: 'var(--muted)' }}> · miners can be collected {dur(minStake)} after they start</span>}
          </div>
          <div>
            {idleShips.length > 0 && <button className="action-btn af-ghost" disabled={busy} onClick={sendToWork(idleShips, `Send ${idleShips.length} ship${idleShips.length > 1 ? 's' : ''} to work`)}>Send all to work ({idleShips.length})</button>}{' '}
            {(hasMiners || hasPirates) && list.some((s) => s.where === 'staked') && <button className="action-btn" data-testid="collect-all" disabled={busy}
              onClick={() => openCollect('Collect all', readyMiners.map((s) => s.id), stakedPirates.map((s) => s.id), false)}>Collect all</button>}
          </div>
        </div>
      )}

      {ships === null ? <p style={{ marginTop: '1rem' }}>Loading ships…</p> : ships.length === 0 ? <p style={{ marginTop: '1rem' }}>No ships yet — mint some first.</p> : (
        <div className="ship-grid" data-testid="ships">
          {list.map((sh) => {
            const more = moreItems(sh);
            return (
              <div key={String(sh.id)} className={`ship-card${sh.kind === 'Pirate' ? ' pirate' : ''}`} data-testid={`ship-${sh.id}`} style={{ position: 'relative' }}>
                {more.length > 0 && <button className="af-more" aria-label="More actions" data-testid={`more-${sh.id}`} onClick={() => setMenu(menu === sh.id ? null : sh.id)}>⋯</button>}
                {menu === sh.id && (
                  <div className="af-menu" role="menu">
                    {more.map(([t, fn]) => <button key={t} role="menuitem" disabled={busy} onClick={() => { setMenu(null); fn(); }}>{t}</button>)}
                  </div>
                )}
                <img src={shipImg(sh)} alt={tierLabel(sh)} style={{ width: 64, height: 64, imageRendering: 'pixelated' }} />
                <h3>#{String(sh.id)}</h3>
                <p className={`tier tier-${String(sh.tier).toLowerCase()}`}>{tierLabel(sh)}</p>
                <p>{sh.kind} · Gen{sh.gen}{sh.kind === 'Pirate' ? ` · Rank ${sh.rank}` : ` · Lv ${sh.level} · Power ${sh.power}`}</p>
                <p className="stat">Condition {sh.durability}/10 <Info label="Condition">Every trip home costs the ship 1 condition point and burns a little ORE as fuel{stats ? ` (${stats.fuelBps / 100}% of the cargo, at least ${fmt(stats.fuelMin, 0)} ORE)` : ''}. Repair it from the ⋯ menu.</Info></p>
                <p style={{ color: 'var(--muted)', fontSize: '.85rem' }}>{stateLine(sh)}</p>
                {sh.where === 'staked' && <p className="price">+{fmt(sh.pending, 0)} ORE</p>}
                {mainAction(sh)}
              </div>
            );
          })}
        </div>
      )}

      {openReqs.length > 0 && (
        <div className="card" style={{ marginTop: '1rem' }} data-testid="claims">
          <h3>On the way</h3>
          {openReqs.slice(0, 8).map((r) => (
            <p key={String(r.id)} style={{ marginTop: '.4rem' }}>{r.n} miner{r.n > 1 ? 's' : ''} {r.unstake ? 'coming home' : 'bringing cargo'} · {fmt(r.total, 0)} ORE · {r.route ? 'gamble' : 'safe'} · <span data-testid="claim-state">{stuck(r) ? 'taking longer than usual' : 'Bringing your ORE home…'}</span>
              {stuck(r) && <button title="Only needed if your ORE did not arrive by itself" className="action-btn" data-testid="claim-receive" style={{ marginLeft: '1rem', marginTop: 0 }} disabled={busy} onClick={() => receive(r)}>Receive ORE</button>}</p>
          ))}
        </div>
      )}

      <div className="card" style={{ marginTop: '1rem' }} data-testid="gen1">
        <h3>Build Gen1 ship <Info label="How Gen1 pricing works">Priced in US dollars and paid in ORE at the lower of the 30-minute average and the live ORE price. All the ORE you pay is burned. {stats ? `${stats.hijack}%` : 'A small share'} of new ships are hijacked by a random raiding pirate.</Info></h3>
        {stats && stats.g1s === 0 ? <p data-testid="gen1-wait">Opens {dur(stats.g1d)} after the ORE pool opens.</p> : stats && stats.g1s > now ? <p>Opens {new Date(stats.g1s * 1000).toLocaleString()}</p> : (
          <p>
            <button className="af-step" aria-label="One less" onClick={() => setGen1({ ...gen1, n: Math.max(1, gen1.n - 1) })}>−</button>
            <span className="price" style={{ margin: '0 .6rem' }}>{gen1.n}</span>
            <button className="af-step" aria-label="One more" onClick={() => setGen1({ ...gen1, n: Math.min(10, gen1.n + 1) })}>+</button>
            {' '}<span className="price">{gen1.usd !== undefined && gen1.ok ? `$${fmt(gen1.usd, 2)}` : '…'}</span>
            {gen1.ok ? <span data-testid="gen1-ore" style={{ color: 'var(--muted)' }}> (about {fmt(gen1.cost, 0)} ORE)</span> : gen1.why ? <span data-testid="gen1-why" style={{ color: 'var(--muted)' }}> · {gen1.why === 'NetworkBusy' ? 'price unavailable right now' : reasonText(gen1.why)}</span> : null}
            <span style={{ color: 'var(--muted)' }}> · you have {fmt(ore, 0)} ORE</span>
            {' '}<button className="action-btn" style={{ marginTop: 0, marginLeft: '.6rem' }} disabled={!gen1.ok || busy} onClick={mintGen1}>Build</button>
          </p>
        )}
        {g1pend.filter((p) => p.status !== 3).map((p) => (
          <p key={String(p.pid)} style={{ marginTop: '.4rem' }}>{p.count} ship{p.count > 1 ? 's' : ''} being built · {p.status === 1 ? 'rolling the dice…' : 'delivering…'}
            {p.status === 2 && <button title="Only needed if automatic delivery is stuck" className="action-btn" style={{ marginLeft: '1rem', marginTop: 0 }} disabled={busy} onClick={act('Deliver', async () => { await send('Deliver your new ships', (s) => contract('Shipyard', s).settleGen1(p.pid)); })}>Deliver now</button>}</p>
        ))}
      </div>

      {collect && (
        <Modal title={collect.title} testid="collect-modal" onClose={() => setCollect(null)}>
          <p className="cm-sub">{collect.miners.length} miner{collect.miners.length !== 1 ? 's' : ''}{collect.pirates.length ? ` and ${collect.pirates.length} pirate${collect.pirates.length > 1 ? 's' : ''}` : ''}. How should your miners bring the ORE home?</p>
          <button className={`cm-wallet${route === 0 ? ' af-on' : ''}`} data-testid="route-safe" onClick={() => setRoute(0)}>
            <span className="cm-ico">🛡️</span><span className="cm-name">Safe<br /><small style={{ color: 'var(--muted)' }}>Pirates take {stats?.safeTax ?? 30}%, you keep the rest.</small></span>{route === 0 && <span className="cm-tag">✓</span>}
          </button>
          <button className={`cm-wallet${route === 1 ? ' af-on' : ''}`} data-testid="route-gamble" onClick={() => setRoute(1)}>
            <span className="cm-ico">🎲</span><span className="cm-name">Gamble<br /><small style={{ color: 'var(--muted)' }}>Pirates take only {stats?.riskyTax ?? 15}%, but there is a 1 in {Math.round(100 / (stats?.intercept || 10))} chance they steal it all.</small></span>{route === 1 && <span className="cm-tag">✓</span>}
          </button>
          <p className="cm-note" style={{ color: 'var(--muted)' }}>A little ORE is burned as fuel and each ship loses 1 condition point. Your ORE arrives by itself a few seconds later.</p>
          <button className="action-btn" data-testid="collect-go" disabled={busy} onClick={doCollect}>{collect.unstake ? 'Bring home' : 'Collect'}</button>
        </Modal>
      )}

      {sell && (
        <Modal title={`Sell ship #${sell.ship.id}`} testid="sell-modal" onClose={() => setSell(null)}>
          <p className="cm-sub">{tierLabel(sell.ship)} {sell.ship.kind}. Buyers pay in {NET.currency}. A 5% market fee is taken when it sells; you can cancel any time before that.</p>
          <div className="elig-row">
            <input data-testid="sell-price" inputMode="decimal" value={sell.price} onChange={(e) => setSell({ ...sell, price: e.target.value })} aria-label={`Price in ${NET.currency}`} />
            <span className="price" style={{ alignSelf: 'center' }}>{NET.currency}</span>
          </div>
          <p className="cm-sub" data-testid="sell-usd" style={{ marginTop: '.5rem' }}>{sellUsd !== null ? `≈ $${sellUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : '\u00a0'}</p>
          <button className="action-btn" data-testid="sell-go" disabled={busy} onClick={doSell}>Put up for sale</button>
        </Modal>
      )}
    </div>
  );
}

export default Dashboard;
